<template lang="pug">
  .container
    suggest-topic-modal(v-if='showSuggestTopicModal')
    delete-krea-class-modal(v-if='showDeleteKreaClassModal')
    krea-publish-content-modal(v-if='showKreaPublishContentModal' @success='goToPublishedList')
    progress-bar(v-if='contentCompleteness')
    .main-container.max-content
      choice-modal(v-if='showChoiceModal')
      loader-toast.absolute(:isSaving='isSaving' :error='savingError')
      .updated-at.row.middle.m-bottom-05(v-if='this.resource.modifiedBy')
        span.text-small.dark-gray.m-right-1 {{ $t("krea.resource.updatedAt", {date: updatedAtDate}) }}
        span.text-small.dark-gray.author.row.middle {{ updatedByLabel }}
      .row.middle.m4-bottom-3.space-between
        krea-topics-input#subtopics(
          edit
          v-if='resource'
          :info='topicsInfo'
          :subtopics='resource.subtopicIds'
          :updateMethod='updateSubtopics'
        )
        .row.self-start.m4-top-5
          language-selector.row.middle.center(
            v-if='availableLanguages'
            :languages='availableLanguages'
            @change-resource-locale='updateLocale'
            inner
          )
      router-link.program.middle.m4-bottom-5(v-if='resource' :to='{name: routes.kreaEducationalContent, params: {id: resource.educationalProgramId}}')
        simple-svg.m4-top-1.dark-gray-icon.m-right-2(:filepath='arrowLeftIcon' width='15px' height='15px')
        simple-svg.m4-top-1.dark-gray-icon.m-right-1(:filepath='layers' width='15px' height='15px')
        p.text-normal.dark-gray(v-if='resource.educationalProgramNames') {{ educationalProgramName || $t("programCard.untitled") }}
      .row.space-between.m-bottom-5#title
        krea-text-area.m4-top-3.item-1(
          v-if='resource'
          :info='titleInfo'
          edit
          name='title'
          programTitle
          :value='activeLocalizedResource.title'
          :updateMethod='updateResourceTitle'
          :maxCharacters='50'
          :placeholder='innerI18n.t("krea.resource.titlePlaceholder")'
          size='title-big'
          height='100px'
        )
      .tab-selection.width-100.row.relative
        router-link.tab-selector.text-big.row.center(
          v-if='resource'
          v-for='(tab, index) in tabs'
          :key='index'
          :to='{name: tab.route}'
          :class='{ "active-tab": isActiveRoute(tab.route) }'
        ) {{ innerI18n.t('teacher.tab', { title: tab.name }) }}
      .tab-container(v-if='resource')
        router-view
      add-krea-resource-language-modal(v-if='showAddKreaResourceLanguageModal' @addedLanguage='reloadResource')
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { i18n, innerI18n } from '../main'
import LanguageSelector from '@/components/LanguageSelector'
import KreaTextArea from '@/components/Krea/Inputs/KreaTextArea'
import { tabTitle } from '@/utils/constants'
import LoaderToast from '@/components/Krea/LoaderToast'
import layers from '@/assets/ic_layers.svg'
import arrowLeftIcon from '@/assets/ic_chevron-left.svg'
import { languagesCodesToString, supportedLanguages } from '../constants/languages'
import { routes } from '../router'
import Loader from '@/components/Loader'
import AddKreaResourceLanguageModal from '@/components/Modals/AddKreaResourceLanguageModal'
import ChoiceModal from '@/components/Modals/ChoiceModal'
import ProgressBar from '@/components/Krea/EducationalContent/ProgressBar/ProgressBar'
import KreaPublishContentModal from '@/components/Modals/KreaPublishContentModal'
import KreaTopicsInput from '@/components/Krea/Resource/KreaTopicsInput'
import SuggestTopicModal from '@/components/Krea/Resource/SuggestTopicModal'
import kreaResourceRedirection from '@/mixins/kreaResourceRedirection'
import DeleteKreaClassModal from '../components/Modals/DeleteKreaClassModal'
import moment from 'moment'

export default {
  mixins: [kreaResourceRedirection],
  components: {
    DeleteKreaClassModal,
    SuggestTopicModal,
    ChoiceModal,
    KreaTopicsInput,
    KreaPublishContentModal,
    Loader,
    AddKreaResourceLanguageModal,
    ProgressBar,
    LoaderToast,
    KreaTextArea,
    LanguageSelector
  },
  data() {
    return {
      arrowLeftIcon,
      innerI18n,
      routes,
      layers
    }
  },
  watch: {
    '$route.params.id'() {
      this.reloadResource()
    },
    isSaving(newState) {
      if (!newState) this.reloadCompleteness()
    },
    innerLocale() {
      this.reloadCompleteness()
    }
  },
  async created() {
    this.showElement({ element: 'fullPageLoader' })
    await this.getUserCustomFields()
    await this.hydrateOds()
    await this.hydrateAges()
    await this.hydrateSkills()
    await this.hydrateSubjects()
    await this.hydrateEvaluationMethods()
    await this.hydrateCategories()
    await this.hydrateKreaResource({ id: this.$route.params.id, locale: this.locale })
    this.changeInnerLocale(this.activeLanguage)
    await this.hydrateKreaEducationalContentLanguageCompleteness(this.resource.educationalProgramId)
    await this.hydrateKreaEducationalContentCompleteness({
      id: this.resource.educationalProgramId,
      locale: this.activeLanguage
    })
    if (this.resourceAvailableLanguages.includes(this.$route.query.locale)) this.updateLocale(this.$route.query.locale)
    this.hideElement({ element: 'fullPageLoader' })
  },
  computed: {
    ...mapState({
      resource: state => state.kreaResource.resource,
      showSuggestTopicModal: state => state.behavior.suggestTopicModal.show,
      showKreaPublishContentModal: state => state.behavior.kreaPublishContentModal.show,
      activeLanguage: state => state.kreaResource.activeLanguage,
      contentCompleteness: state => state.kreaEducationalContent.contentCompleteness,
      isSaving: state => state.kreaResource.isSaving,
      savingError: state => state.kreaResource.savingError,
      categories: state => state.dashboard.categories,
      locale: state => state.behavior.locale,
      innerLocale: state => state.behavior.innerLocale,
      skills: state => state.dashboard.skills,
      subjects: state => state.profile.subjects,
      ages: state => state.dashboard.ages,
      showChoiceModal: state => state.behavior.choiceModal.show,
      showDeleteKreaClassModal: state => state.behavior.deleteKreaClassModal.show,
      showAddKreaResourceLanguageModal: state => state.behavior.addKreaResourceLanguageModal.show,
      customFields: state => state.userInfo.customFields,
      userInfo: state => state.userInfo.userInfo
    }),
    updatedAtDate() {
      return moment(this.resource.updatedAt).format('DD/MM/YYYY')
    },
    updatedByLabel() {
      return this.resource.modifiedBy === this.userInfo.userId
        ? i18n.t('krea.resource.updatedAtByYou')
        : i18n.t('krea.resource.updatedAtBy', { name: this.resource.modifiedByName })
    },
    kreaContentGuide() {
      return process.env[`VUE_APP_KREA_CREATE_CONTENT_GUIDE_${this.innerLocale}`]
    },
    tabs() {
      return [
        { route: routes.kreaResourcePresentation, name: innerI18n.t('presentationTab.presentation') },
        { route: routes.kreaResourceTeacher, name: innerI18n.t('presentationTab.teacher') },
        { route: routes.kreaResourceStudent, name: innerI18n.t('presentationTab.student') }
      ]
    },
    resourceCategory() {
      return this.resource && this.categories.find(cat => cat.id === this.resource.categoryId)
    },
    resourceLogo() {
      return this.resourceCategory ? this.resourceCategory.imageURL : ''
    },
    tabComponent() {
      return `${this.tabs[this.activeTab].component}-tab`
    },
    viewTitle() {
      return this.resource ? `${tabTitle} - ${this.resource.title}` : tabTitle
    },
    availableLanguages() {
      return this.resource && this.resource.locales
    },
    activeLocalizedResource() {
      return this.resource && this.resource.localizedResources.find(res => languagesCodesToString[res.locale] === this.activeLanguage)
    },
    resourceAvailableLanguages() {
      return supportedLanguages
        .filter(lang => this.availableLanguages.includes(lang.lang))
        .map(lang => lang.lang)
    },
    titleInfo() {
      return {
        title: i18n.t('krea.resource.titleInfoTitle'),
        guideLink: { msg: innerI18n.t('krea.educationalContent.details.contentGuideName'), link: this.kreaContentGuide },
        tips: [i18n.t('krea.resource.titleInfo')]
      }
    },
    topicsInfo() {
      return {
        title: i18n.t('krea.resource.topicsInfoTitle'),
        guideLink: { msg: innerI18n.t('krea.educationalContent.details.contentGuideName'), link: this.kreaContentGuide },
        tips: [i18n.t('krea.resource.topicsInfo')]
      }
    },
    educationalProgramName() {
      return this.resource && this.resource.educationalProgramNames[this.activeLanguage]
    }
  },
  beforeDestroy() {
    this.clearKreaResource()
  },
  methods: {
    ...mapActions([
      'getUserCustomFields',
      'clearKreaResource',
      'hydrateCategories',
      'editKreaLocalizedResourceField',
      'editKreaResourceField',
      'hydrateKreaEducationalContentCompleteness',
      'hydrateKreaEducationalContentLanguageCompleteness',
      'setKreaActiveResourceLanguage',
      'hydrateKreaResource',
      'showElement',
      'hideElement',
      'getShareableLink',
      'changeInnerLocale',
      'hydrateOds',
      'hydrateSkills',
      'hydrateSubjects',
      'hydrateAges',
      'hydrateEvaluationMethods'
    ]),
    goToPublishedList() {
      this.hideElement({ element: 'kreaPublishContentModal' })
      this.$router.push({ name: routes.kreaPublished })
    },
    async reloadResource(locale) {
      await this.hydrateKreaResource({ id: this.$route.params.id, locale: locale || this.$route.query.locale || this.locale })
      this.setKreaActiveResourceLanguage(locale || this.activeLanguage)
      this.changeInnerLocale(locale || this.activeLanguage)
      await this.reloadCompleteness()
    },
    async reloadCompleteness() {
      await this.hydrateKreaEducationalContentLanguageCompleteness(this.resource.educationalProgramId)
      await this.hydrateKreaEducationalContentCompleteness({
        id: this.resource.educationalProgramId,
        locale: this.activeLanguage
      })
    },
    updateLocale(locale) {
      this.changeInnerLocale(locale)
      this.setKreaActiveResourceLanguage(locale)
    },
    updateResourceTitle(value, field) {
      this.editKreaLocalizedResourceField({ field, value, locale: this.activeLanguage })
    },
    updateSubtopics(value) {
      this.editKreaResourceField({ field: 'subtopicIds', value })
    },
    isActiveRoute(route) {
      if (route === routes.kreaResourcePresentation) return this.$route.name === routes.kreaResourcePresentation
      if (route === routes.kreaResourceTeacher) return this.$route.path.includes('/t')
      if (route === routes.kreaResourceStudent) return this.$route.path.includes('/s')
    }
  }
}
</script>

<style lang="scss" scoped>
@import 'src/scss/variables/colors';
@import 'src/scss/variables/sizes';
@import 'src/scss/variables/z-indexes';

.program{
  display: inline-flex;
}

.main-container {
  margin: 0 auto;
  min-height: calc(100vh - #{$navbar-height});
  padding-top: 30px;
}

.title {
  max-width: 748px;
}

.save {
  --max-width: auto;
}

.resource-logo {
  margin-right: 10px;
  width: 35px;
}

.tab-selection {
  width: 450px;
  &>:not(:last-child){
    margin-right: 15px;
  }
}

.updated-at{
  .author{
    &::before{
      margin-right: 8px;
      margin-top: 3px;
      display: block;
      content: "";
      width: 8px;
      height: 8px;
      background-color: $blue;
      border-radius: 50%;
    }
  }
}

.tab-selector {
  width: 140px;
  position: relative;
  padding-bottom: 15px;
  &:hover {
    color: $blue;
  }
  &::after{
    bottom: 0;
    content: "";
    border-radius: 5px;
    height: 5px;
    width: 100%;
    position: absolute;
    background-color: $light-gray;
  }
  &.active-tab {
    &::after{
      background-color: $blue;
    }
    color: $blue;
    font-weight: 700;
  }
}
</style>
